import {
  clearAdsAttributionCookie,
  getAdsAttributionCookie,
  setAdsAttributionCookie,
} from "./adsAttributionCookie";
import {
  installMetaAdsRequestSanitization,
  rewriteMetaAdsRequestUrl,
} from "./adsRequestSanitization";
import {
  getMetaAdsClickId,
  isMetaAdsRolloutCountry,
  META_ADS_CLICK_ID_QUERY_PARAM,
  META_ADS_CLICK_ID_STORAGE_KEY,
} from "./metaAdsClickId";

const META_PIXEL_SCRIPT_ID = "meta-ads-pixel";
const META_PIXEL_SCRIPT_URL = "https://connect.facebook.net/en_US/fbevents.js";
const META_MEASUREMENT_READY_TIMEOUT_MS = 1500;
const META_SCRIPT_LOAD_TIMEOUT_MS = 1500;
const META_ACCOUNT_CREATION_EVENT = "green_hydrogen";
const RESTRICTED_PAGE_URL = "https://ads.openai.com/";
const META_FBC_COOKIE_NAME = "_fbc";
const META_FBC_COOKIE_MAX_AGE_DAYS = 90;
const META_CLICK_ID_CAPTURED_AT_STORAGE_KEY = "tapestry:metaAds:fbclidCapturedAt";
const META_PENDING_CONVERSIONS_STORAGE_KEY = "tapestry:metaAds:pendingAccountCreationConversions";
const META_PENDING_CLICK_IDS_STORAGE_KEY =
  "tapestry:metaAds:pendingAccountCreationConversionClickIds";
const META_TRACKED_CONVERSIONS_STORAGE_KEY = "tapestry:metaAds:trackedAccountCreationConversions";
const MAX_STORED_TRANSACTION_IDS = 20;

type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  loaded?: boolean;
  disablePushState?: boolean;
  push?: (...args: unknown[]) => void;
  queue?: IArguments[];
  version?: string;
};

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

let measurementEnabled = false;
let consentResolved = true;
let pixelId: string | undefined;
let shareAllConversions = false;
let measurementReadyWaiters: Array<(ready: boolean) => void> = [];
let scriptLoaded = false;
let scriptLoadPromise: Promise<boolean> | undefined;
let initializedPixelId: string | undefined;
const inFlightTransactionIds = new Set<string>();
const trackedTransactionIds = new Set<string>();

export { isMetaAdsRolloutCountry };

export function setMetaAdsMeasurementState({
  enabled,
  isConsentResolved,
  shouldShareAllConversions = false,
  configuredPixelId,
}: {
  enabled: boolean;
  isConsentResolved: boolean;
  shouldShareAllConversions?: boolean;
  configuredPixelId: string;
}) {
  measurementEnabled = enabled;
  consentResolved = isConsentResolved;
  shareAllConversions = shouldShareAllConversions;
  pixelId = configuredPixelId || undefined;

  if (measurementEnabled && typeof window !== "undefined") {
    captureMetaAdsClickIdFromCurrentUrl();
    void drainPendingMetaAdsConversions();
  } else if (typeof window !== "undefined" && consentResolved) {
    clearStoredMetaAdsClickId();
    clearAdsAttributionCookie(META_FBC_COOKIE_NAME);
    clearPendingMetaAdsConversions();
    removeMetaAdsClickIdFromCurrentUrl();
    if (initializedPixelId) {
      try {
        window.fbq?.("consent", "revoke");
      } catch (error) {
        console.warn("Failed to revoke Meta Ads measurement consent", { error });
      }
      initializedPixelId = undefined;
    }
    if (!scriptLoaded && window.fbq?.queue) {
      window.fbq.queue = [];
    }
  }
  notifyMeasurementStateChanged();
}

export async function trackMetaAdsAccountCreated(transactionId: string): Promise<void> {
  if (!transactionId || hasTrackedTransactionId(transactionId)) return;
  let clickId = getStoredMetaAdsClickId();
  if (!isMeasurementReady()) {
    if (!(await waitForMeasurementReady())) return;
    clickId = getStoredMetaAdsClickId();
  }
  if (!clickId && !shareAllConversions) return;

  // Persist synchronously so an immediate post-create navigation cannot lose the conversion.
  storePendingTransactionId(transactionId, clickId);
  if (!isMeasurementReady() && !(await waitForMeasurementReady())) return;
  if (!pixelId) return;
  await drainPendingMetaAdsConversions();
}

async function drainPendingMetaAdsConversions(): Promise<void> {
  if (!isMeasurementReady()) return;
  const targetPixelId = pixelId;
  if (!targetPixelId) return;

  const transactionIds = getStoredTransactionIds(META_PENDING_CONVERSIONS_STORAGE_KEY);
  await Promise.all(
    transactionIds.map((transactionId) => {
      const clickId = getPendingClickId(transactionId, getStoredMetaAdsClickId());
      if (!clickId && !shareAllConversions) return Promise.resolve();
      return deliverMetaAdsConversion({ transactionId, clickId, targetPixelId });
    }),
  );
}

async function deliverMetaAdsConversion({
  transactionId,
  clickId,
  targetPixelId,
}: {
  transactionId: string;
  clickId: string | undefined;
  targetPixelId: string;
}): Promise<void> {
  if (hasTrackedTransactionId(transactionId) || inFlightTransactionIds.has(transactionId)) return;
  inFlightTransactionIds.add(transactionId);
  try {
    installMetaAdsRequestSanitization();
    if (clickId) {
      ensureMetaAttributionCookie(clickId);
    } else {
      clearAdsAttributionCookie(META_FBC_COOKIE_NAME);
    }

    const fbq = getOrCreateFbq();
    if (initializedPixelId !== targetPixelId) {
      fbq("consent", "grant");
      fbq.disablePushState = true;
      fbq("set", "autoConfig", "false", targetPixelId);
      fbq("dataProcessingOptions", ["LDU"], 0, 0);
      fbq("init", targetPixelId);
      initializedPixelId = targetPixelId;
    }
    if (
      !(await loadMetaPixelScript()) ||
      !measurementEnabled ||
      (!clickId && !shareAllConversions) ||
      pixelId !== targetPixelId
    ) {
      return;
    }

    const loadedFbq = window.fbq ?? fbq;
    loadedFbq(
      "trackSingleCustom",
      targetPixelId,
      META_ACCOUNT_CREATION_EVENT,
      {
        ...(clickId ? { fbclid: clickId } : {}),
        dl: RESTRICTED_PAGE_URL,
        rl: "",
      },
      {
        eventID: transactionId,
      },
    );
    restoreCurrentMetaAttributionCookie(clickId);
    markTransactionIdTracked(transactionId);
  } catch (error) {
    console.warn("Failed to track Meta Ads account creation conversion", { error });
  } finally {
    inFlightTransactionIds.delete(transactionId);
  }
}

function getStoredTransactionIds(storageKey: string): string[] {
  try {
    const value = JSON.parse(window.sessionStorage.getItem(storageKey) ?? "[]");
    if (!Array.isArray(value)) return [];
    return value.filter((item): item is string => typeof item === "string" && item.length > 0);
  } catch {
    return [];
  }
}

function setStoredTransactionIds(storageKey: string, transactionIds: string[]) {
  try {
    window.sessionStorage.setItem(
      storageKey,
      JSON.stringify(transactionIds.slice(-MAX_STORED_TRANSACTION_IDS)),
    );
  } catch {
    // Measurement storage failures must not block account creation.
  }
}

function storePendingTransactionId(transactionId: string, clickId: string | undefined) {
  const pending = getStoredTransactionIds(META_PENDING_CONVERSIONS_STORAGE_KEY);
  if (pending.includes(transactionId)) return;

  const nextPending = [...pending, transactionId].slice(-MAX_STORED_TRANSACTION_IDS);
  setStoredTransactionIds(META_PENDING_CONVERSIONS_STORAGE_KEY, nextPending);
  setPendingClickId(transactionId, clickId, nextPending);
}

function getPendingClickIds(): Record<string, string | null> {
  try {
    const value = JSON.parse(
      window.sessionStorage.getItem(META_PENDING_CLICK_IDS_STORAGE_KEY) ?? "{}",
    );
    if (!value || typeof value !== "object" || Array.isArray(value)) return {};
    return Object.fromEntries(
      Object.entries(value).filter(
        (entry): entry is [string, string | null] =>
          typeof entry[0] === "string" && (typeof entry[1] === "string" || entry[1] === null),
      ),
    );
  } catch {
    return {};
  }
}

function setPendingClickId(
  transactionId: string,
  clickId: string | undefined,
  pendingTransactionIds: string[],
) {
  try {
    const pendingClickIds = Object.fromEntries(
      Object.entries(getPendingClickIds()).filter(([pendingId]) =>
        pendingTransactionIds.includes(pendingId),
      ),
    );
    pendingClickIds[transactionId] = clickId ?? null;
    window.sessionStorage.setItem(
      META_PENDING_CLICK_IDS_STORAGE_KEY,
      JSON.stringify(pendingClickIds),
    );
  } catch {
    // Measurement storage failures must not block account creation.
  }
}

function getPendingClickId(
  transactionId: string,
  legacyClickId: string | undefined,
): string | undefined {
  const pendingClickIds = getPendingClickIds();
  return Object.prototype.hasOwnProperty.call(pendingClickIds, transactionId) ?
      pendingClickIds[transactionId] ?? undefined
    : legacyClickId;
}

function hasTrackedTransactionId(transactionId: string): boolean {
  return (
    trackedTransactionIds.has(transactionId) ||
    getStoredTransactionIds(META_TRACKED_CONVERSIONS_STORAGE_KEY).includes(transactionId)
  );
}

function markTransactionIdTracked(transactionId: string) {
  trackedTransactionIds.add(transactionId);
  const tracked = getStoredTransactionIds(META_TRACKED_CONVERSIONS_STORAGE_KEY);
  if (!tracked.includes(transactionId)) {
    setStoredTransactionIds(META_TRACKED_CONVERSIONS_STORAGE_KEY, [...tracked, transactionId]);
  }
  setStoredTransactionIds(
    META_PENDING_CONVERSIONS_STORAGE_KEY,
    getStoredTransactionIds(META_PENDING_CONVERSIONS_STORAGE_KEY).filter(
      (pendingId) => pendingId !== transactionId,
    ),
  );
  const pendingClickIds = getPendingClickIds();
  delete pendingClickIds[transactionId];
  try {
    window.sessionStorage.setItem(
      META_PENDING_CLICK_IDS_STORAGE_KEY,
      JSON.stringify(pendingClickIds),
    );
  } catch {
    // Measurement storage failures must not block account creation.
  }
}

function clearPendingMetaAdsConversions() {
  try {
    window.sessionStorage.removeItem(META_PENDING_CONVERSIONS_STORAGE_KEY);
    window.sessionStorage.removeItem(META_PENDING_CLICK_IDS_STORAGE_KEY);
  } catch {
    // Consent revocation must not fail when storage is unavailable.
  }
  inFlightTransactionIds.clear();
}

export function captureMetaAdsClickIdFromCurrentUrl() {
  if (!measurementEnabled || typeof window === "undefined") return;

  const searchParams = new URLSearchParams(window.location.search);
  if (!searchParams.has(META_ADS_CLICK_ID_QUERY_PARAM)) return;

  const clickId = getMetaAdsClickId(searchParams);
  if (clickId) {
    try {
      window.sessionStorage.setItem(META_ADS_CLICK_ID_STORAGE_KEY, clickId);
      window.sessionStorage.setItem(META_CLICK_ID_CAPTURED_AT_STORAGE_KEY, String(Date.now()));
    } catch {
      // The first-party attribution cookie remains available if session storage is unavailable.
    }
    setMetaAttributionCookie(clickId);
  } else {
    try {
      window.sessionStorage.removeItem(META_ADS_CLICK_ID_STORAGE_KEY);
      window.sessionStorage.removeItem(META_CLICK_ID_CAPTURED_AT_STORAGE_KEY);
    } catch {
      // Invalid attribution still clears the first-party cookie below.
    }
    clearAdsAttributionCookie(META_FBC_COOKIE_NAME);
  }
  removeMetaAdsClickIdFromCurrentUrl();
}

export { rewriteMetaAdsRequestUrl };

function getStoredMetaAdsClickId(): string | undefined {
  try {
    const storedClickId = window.sessionStorage.getItem(META_ADS_CLICK_ID_STORAGE_KEY);
    if (storedClickId) {
      const validatedClickId = getMetaAdsClickId(
        new URLSearchParams([[META_ADS_CLICK_ID_QUERY_PARAM, storedClickId]]),
      );
      if (validatedClickId) return validatedClickId;
    }
  } catch {
    // Fall back to Meta's first-party attribution cookie.
  }

  const fbc = getAdsAttributionCookie(META_FBC_COOKIE_NAME);
  const clickId = fbc?.match(/^fb\.1\.\d+\.(.+)$/)?.[1];
  return clickId ?
      getMetaAdsClickId(new URLSearchParams([[META_ADS_CLICK_ID_QUERY_PARAM, clickId]]))
    : undefined;
}

function clearStoredMetaAdsClickId() {
  try {
    window.sessionStorage.removeItem(META_ADS_CLICK_ID_STORAGE_KEY);
    window.sessionStorage.removeItem(META_CLICK_ID_CAPTURED_AT_STORAGE_KEY);
  } catch {
    // Consent revocation must not fail when storage is unavailable.
  }
}

function setMetaAttributionCookie(clickId: string) {
  setAdsAttributionCookie({
    name: META_FBC_COOKIE_NAME,
    value: `fb.1.${getMetaClickIdCapturedAt()}.${clickId}`,
    maxAgeDays: META_FBC_COOKIE_MAX_AGE_DAYS,
  });
}

function ensureMetaAttributionCookie(clickId: string) {
  if (getMetaClickIdFromAttributionCookie() === clickId) return;
  setMetaAttributionCookie(clickId);
}

function restoreCurrentMetaAttributionCookie(deliveredClickId: string | undefined) {
  const currentClickId = getStoredMetaAdsClickId();
  if (currentClickId && currentClickId !== deliveredClickId) {
    setMetaAttributionCookie(currentClickId);
  }
}

function getMetaClickIdFromAttributionCookie(): string | undefined {
  return getAdsAttributionCookie(META_FBC_COOKIE_NAME)?.match(/^fb\.1\.\d+\.(.+)$/)?.[1];
}

function getMetaClickIdCapturedAt(): number {
  try {
    const capturedAt = Number(window.sessionStorage.getItem(META_CLICK_ID_CAPTURED_AT_STORAGE_KEY));
    if (Number.isSafeInteger(capturedAt) && capturedAt > 0) return capturedAt;
  } catch {
    // Fall back to the current time when storage is unavailable or from a pre-migration session.
  }
  return Date.now();
}

function removeMetaAdsClickIdFromCurrentUrl() {
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete(META_ADS_CLICK_ID_QUERY_PARAM);
    window.history.replaceState(
      window.history.state,
      "",
      `${url.pathname}${url.search}${url.hash}`,
    );
  } catch {
    // URL cleanup must not block conversion tracking.
  }
}

function getOrCreateFbq(): Fbq {
  if (window.fbq) return window.fbq;

  const fbq = function metaPixelQueue() {
    // Match Meta's bootstrap queue shape so fbevents.js can process commands.
    // eslint-disable-next-line prefer-rest-params
    fbq.callMethod ? fbq.callMethod(...Array.from(arguments)) : fbq.queue?.push(arguments);
  } as Fbq;
  fbq.queue = [];
  fbq.loaded = true;
  fbq.version = "2.0";
  window.fbq = fbq;
  window._fbq = fbq;
  return fbq;
}

function loadMetaPixelScript(): Promise<boolean> {
  if (scriptLoaded) return Promise.resolve(true);
  if (scriptLoadPromise) return scriptLoadPromise;

  const existingScript = document.getElementById(META_PIXEL_SCRIPT_ID);
  if (existingScript instanceof HTMLScriptElement) {
    scriptLoadPromise = waitForScriptLoad(existingScript);
    return scriptLoadPromise;
  }

  const script = document.createElement("script");
  script.id = META_PIXEL_SCRIPT_ID;
  script.async = true;
  script.src = META_PIXEL_SCRIPT_URL;
  scriptLoadPromise = waitForScriptLoad(script);
  document.head.appendChild(script);
  return scriptLoadPromise;
}

function waitForScriptLoad(script: HTMLScriptElement): Promise<boolean> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (loaded: boolean) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      resolve(loaded);
    };
    const timeoutId = window.setTimeout(() => {
      scriptLoadPromise = undefined;
      finish(false);
    }, META_SCRIPT_LOAD_TIMEOUT_MS);
    script.addEventListener(
      "load",
      () => {
        scriptLoaded = true;
        scriptLoadPromise = Promise.resolve(true);
        finish(true);
        void drainPendingMetaAdsConversions();
      },
      { once: true },
    );
    script.addEventListener(
      "error",
      () => {
        scriptLoadPromise = undefined;
        initializedPixelId = undefined;
        if (window.fbq?.queue) {
          window.fbq.queue = [];
        }
        script.remove();
        finish(false);
      },
      { once: true },
    );
  });
}

function isMeasurementReady(): boolean {
  return measurementEnabled && typeof window !== "undefined" && pixelId != null;
}

function shouldStopWaitingForMeasurementReady(): boolean {
  return consentResolved && !measurementEnabled;
}

function waitForMeasurementReady(): Promise<boolean> {
  if (isMeasurementReady()) return Promise.resolve(true);
  if (typeof window === "undefined" || shouldStopWaitingForMeasurementReady()) {
    return Promise.resolve(false);
  }

  return new Promise((resolve) => {
    let settled = false;
    const finish = (ready: boolean) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      measurementReadyWaiters = measurementReadyWaiters.filter((waiter) => waiter !== finish);
      resolve(ready);
    };
    const timeoutId = window.setTimeout(() => finish(false), META_MEASUREMENT_READY_TIMEOUT_MS);
    measurementReadyWaiters.push(finish);
  });
}

function notifyMeasurementStateChanged() {
  if (!isMeasurementReady() && !shouldStopWaitingForMeasurementReady()) return;

  const ready = isMeasurementReady();
  const waiters = measurementReadyWaiters;
  measurementReadyWaiters = [];
  for (const waiter of waiters) waiter(ready);
}
